const sanitizeSegment = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");

const getConfiguredLanBaseUrl = () =>
    trimTrailingSlash(process.env.REACT_APP_LAN_BASE_URL || "");

const isLocalDevHostname = (hostname = "") =>
    /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/i.test(String(hostname || "").trim());

const getBrowserOrigin = () => {
    if (typeof window === "undefined") return "";
    const configuredLanBaseUrl = getConfiguredLanBaseUrl();
    const browserLocation = window.location || {};
    const browserOrigin = browserLocation.origin || "";

    if (configuredLanBaseUrl && isLocalDevHostname(browserLocation.hostname)) {
        return configuredLanBaseUrl;
    }

    return browserOrigin;
};

const toAbsoluteUrl = (value = "") => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "";

    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }

    const origin = getBrowserOrigin();
    if (!origin) return trimmed;

    return `${trimTrailingSlash(origin)}/${trimmed.replace(/^\/+/, "")}`;
};

export const makeUrlMobileReachable = (value = "") => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return "";

    const configuredLanBaseUrl = getConfiguredLanBaseUrl();
    if (!configuredLanBaseUrl || typeof window === "undefined") {
        return trimmed;
    }

    try {
        const currentOrigin = window.location?.origin || "";
        if (!currentOrigin) {
            return trimmed;
        }

        return trimmed.replace(currentOrigin, configuredLanBaseUrl);
    } catch (error) {
        return trimmed;
    }
};

export const extractShareToken = (shareLink = "") => {
    if (!shareLink) return "";

    const trimmed = String(shareLink).trim();
    const parts = trimmed.split("/").filter(Boolean);

    return parts.length ? parts[parts.length - 1] : "";
};

const applyTemplate = (template, replacements) =>
    Object.entries(replacements).reduce((output, [key, value]) => {
        const safeValue = encodeURIComponent(String(value || ""));
        return output.replace(new RegExp(`\\{${key}\\}`, "g"), safeValue);
    }, template);

export const resolveAndroidHostedModelUrl = ({
    shareLink = "",
    buildingTypeName = "",
    width = "",
    length = "",
    height = "",
    manufacturerId = "",
} = {}) => {
    const directUrl = process.env.REACT_APP_AR_ANDROID_MODEL_URL || "";
    if (directUrl) {
        return directUrl;
    }

    const shareToken = extractShareToken(shareLink);
    const template = process.env.REACT_APP_AR_ANDROID_MODEL_TEMPLATE || "";
    if (template) {
        return applyTemplate(template, {
            shareToken,
            buildingType: sanitizeSegment(buildingTypeName),
            width,
            length,
            height,
            manufacturerId,
        });
    }

    const baseUrl = process.env.REACT_APP_AR_ANDROID_MODEL_BASE_URL || "";
    if (baseUrl && shareToken) {
        return `${trimTrailingSlash(baseUrl)}/${encodeURIComponent(shareToken)}.glb`;
    }

    const fallbackPath =
        process.env.REACT_APP_AR_ANDROID_SAMPLE_PATH ||
        "/assets/glbModels/pattern_1.glb";

    return toAbsoluteUrl(fallbackPath);
};

export const describeAndroidHostedModelSource = ({
    shareLink = "",
} = {}) => {
    if (process.env.REACT_APP_AR_ANDROID_MODEL_URL) {
        return "direct-url";
    }

    if (process.env.REACT_APP_AR_ANDROID_MODEL_TEMPLATE) {
        return "template-url";
    }

    if (
        process.env.REACT_APP_AR_ANDROID_MODEL_BASE_URL &&
        extractShareToken(shareLink)
    ) {
        return "share-token-base-url";
    }

    return "same-origin-sample";
};

export const resolveIosHostedModelUrl = ({
    shareLink = "",
    buildingTypeName = "",
    width = "",
    length = "",
    height = "",
    manufacturerId = "",
} = {}) => {
    const directUrl = process.env.REACT_APP_AR_IOS_MODEL_URL || process.env.REACT_APP_AR_USDZ_URL || "";
    if (directUrl) {
        return directUrl;
    }

    const shareToken = extractShareToken(shareLink);
    const template = process.env.REACT_APP_AR_IOS_MODEL_TEMPLATE || "";
    if (template) {
        return applyTemplate(template, {
            shareToken,
            buildingType: sanitizeSegment(buildingTypeName),
            width,
            length,
            height,
            manufacturerId,
        });
    }

    const baseUrl = process.env.REACT_APP_AR_IOS_MODEL_BASE_URL || "";
    if (baseUrl && shareToken) {
        return `${trimTrailingSlash(baseUrl)}/${encodeURIComponent(shareToken)}.usdz`;
    }

    const fallbackPath = process.env.REACT_APP_AR_IOS_SAMPLE_PATH || "";
    return fallbackPath ? toAbsoluteUrl(fallbackPath) : "";
};

export const describeIosHostedModelSource = ({
    shareLink = "",
} = {}) => {
    if (process.env.REACT_APP_AR_IOS_MODEL_URL || process.env.REACT_APP_AR_USDZ_URL) {
        return "direct-url";
    }

    if (process.env.REACT_APP_AR_IOS_MODEL_TEMPLATE) {
        return "template-url";
    }

    if (process.env.REACT_APP_AR_IOS_MODEL_BASE_URL && extractShareToken(shareLink)) {
        return "share-token-base-url";
    }

    if (process.env.REACT_APP_AR_IOS_SAMPLE_PATH) {
        return "same-origin-sample";
    }

    return "not-configured";
};
